import { useQuery } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Card } from '@ovComponents/2-component/card/card';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import {
  startTransition,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { FETCH_UPCOMING_TRANSFERS } from '../queries';
import { LABEL_SX, SECONDARY_TEXT_SX, SMALL_SX } from '../styles';
import { useTradingHub } from '../tradingHubContext';

type Insight = {
  id: string;
  type: 'alert' | 'suggestion' | 'info' | 'warning';
  title: string;
  description: string;
  action?: string;
  onAction?: () => void;
};

const InsightRule = ({ insight, sys }: { insight: Insight; sys: any }) => {
  const iconColors: Record<Insight['type'], string> = {
    alert: sys.color.negative,
    warning: sys.color.warning,
    suggestion: sys.color.primary,
    info: sys.color.onSurfaceVariant,
  };
  const borderColors: Record<Insight['type'], string> = {
    alert: sys.color.negative,
    warning: sys.color.warning,
    suggestion: sys.color.primary,
    info: sys.color.outlineVariant,
  };
  return (
    <Card
      sx={{
        mb: 1,
        borderLeft: '3px solid',
        borderLeftColor: borderColors[insight.type],
        p: 1.25,
      }}
    >
      <Box display='flex' alignItems='flex-start' gap={0.75}>
        {insight.type === 'alert' && (
          <ErrorOutlineIcon sx={{ fontSize: 14, color: iconColors.alert }} />
        )}
        {insight.type === 'warning' && (
          <WarningAmberIcon sx={{ fontSize: 14, color: iconColors.warning }} />
        )}
        {insight.type === 'suggestion' && (
          <LightbulbIcon sx={{ fontSize: 14, color: iconColors.suggestion }} />
        )}
        {insight.type === 'info' && (
          <InfoOutlinedIcon sx={{ fontSize: 14, color: iconColors.info }} />
        )}
        <Box flex={1}>
          <Typography variant='bodySmall' sx={{ fontWeight: 700, mb: 0.25 }}>
            {insight.title}
          </Typography>
          <Typography variant='bodySmall' sx={SECONDARY_TEXT_SX}>
            {insight.description}
          </Typography>
          {insight.action && insight.onAction && (
            <Typography
              variant='bodySmall'
              sx={{
                color: sys.color.primary,
                cursor: 'pointer',
                fontWeight: 600,
                mt: 0.5,
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={insight.onAction}
            >
              {insight.action}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

const InsightSidebar = () => {
  const { sys } = useThemeTokens();
  const {
    statusBarStats,
    setInsightSidebarOpen,
    setCurrentTab,
    setPortfolioStatusPreFilter,
    setTradeStatusPreFilter,
    setOperationsPreFilter,
  } = useTradingHub();
  const { activeOrganization } = useContext(UserContext);
  const [lastUpdated] = useState(() => new Date());

  const { data: upcomingData } = useQuery(FETCH_UPCOMING_TRANSFERS, {
    variables: {
      input: {
        filter: { organizationId: activeOrganization?.id },
        pagination: {
          perPage: 100,
          sortField: 'scheduledDate',
          sortDesc: false,
        },
      },
    },
    skip: !activeOrganization?.id,
    fetchPolicy: 'cache-first',
  });

  const cashFlowInsights = useMemo(() => {
    const transfers =
      upcomingData?.fetchUpcomingTransfers?.upcomingTransfers || [];
    if (transfers.length === 0)
      return {
        totalCount: 0,
        outflowCount: 0,
        inflowCount: 0,
        imminentCount: 0,
        atRiskCount: 0,
        totalOutflowCents: 0,
      };
    const now = Date.now();
    let outflowCount = 0,
      inflowCount = 0,
      imminentCount = 0,
      atRiskCount = 0,
      totalOutflowCents = 0;
    transfers.forEach((t: any) => {
      const isOutflow = t.type === 'WITHDRAW' || t.type === 'withdraw';
      if (isOutflow) {
        outflowCount++;
        totalOutflowCents += t.amountCents || 0;
        const mvCents = t.subAccount?.statistics?.marketValueCents || 0;
        if (t.amountCents > mvCents * 0.9) atRiskCount++;
      } else inflowCount++;
      if (t.scheduledDate) {
        const diffDays = Math.ceil(
          (new Date(t.scheduledDate).getTime() - now) / 86_400_000,
        );
        if (diffDays <= 1) imminentCount++;
      }
    });
    return {
      totalCount: transfers.length,
      outflowCount,
      inflowCount,
      imminentCount,
      atRiskCount,
      totalOutflowCents,
    };
  }, [upcomingData]);

  const navigateTo = useCallback(
    (
      tab: string,
      preFilter?: { portfolio?: string; trade?: string; operations?: string },
    ) => {
      setInsightSidebarOpen(false);
      startTransition(() => {
        if (preFilter?.portfolio)
          setPortfolioStatusPreFilter(preFilter.portfolio);
        if (preFilter?.trade) setTradeStatusPreFilter(preFilter.trade);
        if (preFilter?.operations) setOperationsPreFilter(preFilter.operations);
        setCurrentTab(tab as any);
      });
    },
    [
      setCurrentTab,
      setInsightSidebarOpen,
      setPortfolioStatusPreFilter,
      setTradeStatusPreFilter,
      setOperationsPreFilter,
    ],
  );

  const totalPortfolios =
    statusBarStats.needsAction +
    statusBarStats.inProgress +
    statusBarStats.onTrack;

  const ruleBasedInsights: Insight[] = useMemo(() => {
    const rules: Insight[] = [];
    if (statusBarStats.needsAction > 0) {
      const urgencyPct =
        totalPortfolios > 0
          ? Math.round((statusBarStats.needsAction / totalPortfolios) * 100)
          : 0;
      rules.push({
        id: 'drift-alert',
        type: 'alert',
        title: `${statusBarStats.needsAction} portfolio${statusBarStats.needsAction > 1 ? 's' : ''} need attention`,
        description: `${urgencyPct}% of portfolios have drift beyond tolerance, pending liquidations, or transfer rebalances. Prioritize these before end of day.`,
        action: 'View flagged portfolios',
        onAction: () =>
          navigateTo('portfolios', { portfolio: 'ANY_REBALANCE' }),
      });
    }
    if (statusBarStats.activeOperations > 0) {
      rules.push({
        id: 'ops-active',
        type: 'warning',
        title: `${statusBarStats.activeOperations} operation${statusBarStats.activeOperations > 1 ? 's' : ''} in progress`,
        description:
          'Active rebalances or trade batches are processing. Check for any that may be stuck in an intermediate state.',
        action: 'Monitor active operations',
        onAction: () => navigateTo('operations', { operations: 'ACTIVE' }),
      });
    }
    if (statusBarStats.tradesInFlight > 0) {
      const isBulk = statusBarStats.tradesInFlight > 50;
      rules.push({
        id: 'trades-flight',
        type: isBulk ? 'warning' : 'info',
        title: `${statusBarStats.tradesInFlight.toLocaleString()} trade${statusBarStats.tradesInFlight > 1 ? 's' : ''} in pipeline`,
        description: isBulk
          ? `Large batch of ${statusBarStats.tradesInFlight.toLocaleString()} trades active. Review for stale INITIATED entries.`
          : 'Trades are progressing through the pipeline. Check for any blocked or stale entries.',
        action: 'View in-flight trades',
        onAction: () => navigateTo('tradeRequests', { trade: 'IN_FLIGHT' }),
      });
    }
    if (cashFlowInsights.atRiskCount > 0) {
      rules.push({
        id: 'cash-flow-risk',
        type: 'alert',
        title: `${cashFlowInsights.atRiskCount} outflow${cashFlowInsights.atRiskCount > 1 ? 's' : ''} at overdraft risk`,
        description:
          'Pending withdrawals exceeding 90% of portfolio value. Could cause overdrafts or forced liquidations.',
        action: 'Review cash flows',
        onAction: () => navigateTo('today'),
      });
    }
    if (cashFlowInsights.imminentCount > 0) {
      rules.push({
        id: 'cash-flow-imminent',
        type: 'warning',
        title: `${cashFlowInsights.imminentCount} cash event${cashFlowInsights.imminentCount > 1 ? 's' : ''} due within 24h`,
        description:
          'Transfers scheduled for today or tomorrow. Verify sufficient liquidity and no conflicting trades.',
        action: 'Review cash flows',
        onAction: () => navigateTo('today'),
      });
    }
    if (
      cashFlowInsights.totalCount > 0 &&
      cashFlowInsights.atRiskCount === 0 &&
      cashFlowInsights.imminentCount === 0
    ) {
      rules.push({
        id: 'cash-flow-overview',
        type: 'info',
        title: `${cashFlowInsights.totalCount} pending cash flow${cashFlowInsights.totalCount > 1 ? 's' : ''}`,
        description: `${cashFlowInsights.inflowCount} inflow${cashFlowInsights.inflowCount !== 1 ? 's' : ''}, ${cashFlowInsights.outflowCount} outflow${cashFlowInsights.outflowCount !== 1 ? 's' : ''}. $${(cashFlowInsights.totalOutflowCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total pending outflows. No immediate risk.`,
        action: 'View details',
        onAction: () => navigateTo('today'),
      });
    }
    if (
      totalPortfolios > 0 &&
      statusBarStats.needsAction === 0 &&
      statusBarStats.activeOperations === 0 &&
      statusBarStats.tradesInFlight === 0
    ) {
      rules.push({
        id: 'all-clear',
        type: 'info',
        title: 'All systems nominal',
        description: `${statusBarStats.onTrack} portfolio${statusBarStats.onTrack !== 1 ? 's' : ''} within tolerance. No active operations or pending trades.`,
      });
    }
    if (
      statusBarStats.totalAum > 0 &&
      statusBarStats.activeOperations === 0 &&
      statusBarStats.needsAction > 3
    ) {
      rules.push({
        id: 'bulk-optimize',
        type: 'suggestion',
        title: 'Consider bulk optimization',
        description: `${statusBarStats.needsAction} portfolios need rebalancing with no active operations. Use bulk select to optimize in one batch.`,
        action: 'Select portfolios',
        onAction: () =>
          navigateTo('portfolios', { portfolio: 'ANY_REBALANCE' }),
      });
    }
    if (
      statusBarStats.tradesInFlight > 0 &&
      statusBarStats.activeOperations === 0
    ) {
      rules.push({
        id: 'settlement-check',
        type: 'suggestion',
        title: 'Review settlement dates',
        description:
          'Trades in pipeline with no active operations. Verify settlement dates are correct.',
        action: 'Check trades',
        onAction: () => navigateTo('tradeRequests', { trade: 'IN_FLIGHT' }),
      });
    }
    return rules;
  }, [statusBarStats, totalPortfolios, cashFlowInsights, navigateTo]);

  const insights = ruleBasedInsights;
  const alertCount = insights.filter(
    (i) => i.type === 'alert' || i.type === 'warning',
  ).length;
  const suggestionCount = insights.filter(
    (i) => i.type === 'suggestion',
  ).length;
  const infoCount = insights.filter((i) => i.type === 'info').length;
  const showSections = insights.length >= 4;

  const timeAgo = useMemo(() => {
    const diffMs = Date.now() - lastUpdated.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    return `${Math.floor(diffMin / 60)}h ago`;
  }, [lastUpdated]);

  const renderSectionHeader = (label: string, count: number) =>
    count > 0 && showSections ? (
      <Typography
        variant='labelSmall'
        sx={{
          ...SMALL_SX,
          color: sys.color.onDisabled,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          mt: 1,
          mb: 0.5,
          px: 0.5,
        }}
      >
        {label} ({count})
      </Typography>
    ) : null;

  const SECTIONS = [
    {
      key: 'alerts',
      label: 'Alerts',
      count: alertCount,
      items: insights.filter((i) => i.type === 'alert' || i.type === 'warning'),
    },
    {
      key: 'suggestions',
      label: 'Suggestions',
      count: suggestionCount,
      items: insights.filter((i) => i.type === 'suggestion'),
    },
    {
      key: 'info',
      label: 'Info',
      count: infoCount,
      items: insights.filter((i) => i.type === 'info'),
    },
  ];
  const alerts = SECTIONS[0].items;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: sys.color.background,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: sys.color.outlineVariant,
        }}
      >
        <Box display='flex' alignItems='center' gap={0.75}>
          <LightbulbIcon sx={{ fontSize: 16, color: sys.color.warning }} />
          <Typography variant='titleMedium'>Insights</Typography>
          {alertCount > 0 && (
            <Box
              sx={{
                minWidth: 18,
                height: 18,
                borderRadius: '9px',
                bgcolor: alerts.some((i) => i.type === 'alert')
                  ? sys.color.negative
                  : sys.color.warning,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.5,
              }}
            >
              <Typography
                variant='bodySmall'
                sx={{
                  ...LABEL_SX,
                  color: sys.color.onPrimary,
                  fontWeight: 700,
                }}
              >
                {alertCount}
              </Typography>
            </Box>
          )}
        </Box>
        <IconButton
          size='small'
          onClick={() => setInsightSidebarOpen(false)}
          label='Close insights'
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 1.25 }}>
        {SECTIONS.map(({ key, label, count, items }) => (
          <Box key={key}>
            {renderSectionHeader(label, count)}
            {items.map((i) => (
              <InsightRule key={i.id} insight={i} sys={sys} />
            ))}
          </Box>
        ))}
        <Box
          sx={{
            mt: 1.5,
            pt: 1,
            borderTop: '1px solid',
            borderColor: sys.color.outlineVariant,
            textAlign: 'center',
          }}
        >
          <Typography
            variant='labelSmall'
            sx={{ ...SMALL_SX, color: sys.color.onDisabled }}
          >
            {'Powered by rules engine'}
            {' \u2022 '}Updated {timeAgo}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default InsightSidebar;
